import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Sheet from 'components/utils/Sheet'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isExternalUrl, removeRegionFromPath } from 'lib/url/searchUrlUtils'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyWhatsNewCards from 'loyalty/hooks/useLoyaltyWhatsNewCards'
import LayoutContainer from 'components/Common/LayoutContainer'
import { LOYALTY_OFFERS_HUB_PAGE } from 'loyalty/constants/loyaltyRoutes'

/** Show the "View all" link to the offers hub only once the carousel holds more than this many cards. */
const VIEW_ALL_MIN_CARDS = 4

const Card = styled(Clickable)`
  display: flex;
  flex-direction: column;
  width: ${rem(280)};
  height: 100%;
  overflow: hidden;
  text-align: left;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${props => props.theme.borderRadius.L};

  ${mediaQueryUp.desktop} {
    width: ${rem(286)};
  }
`

const CardImage = styled.div`
  width: 100%;
  height: ${rem(130)};
  overflow: hidden;
`

const CardBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(20)};
`

const CardCta = styled.div`
  margin-top: auto;
`

interface Props {
  /**
   * Label used in place of the member's tier in the fallback heading — e.g. "Société" for
   * non-members. Only affects the auto-generated fallback; an explicit svc-lab heading still wins.
   */
  audienceLabel?: string
}

function LoyaltyWhatsNewCarousel({ audienceLabel }: Props) {
  const intl = useIntl()
  const { heading, cards } = useLoyaltyWhatsNewCards()
  const [, tierName] = useCurrentLoyaltyTier()

  if (!cards.length) return null

  const sectionHeading = heading ?? intl.formatMessage({
    defaultMessage: 'What’s new for {tier} members',
    id: 'NMc95tDewVpZAQhesjS_',
    description: 'Heading for the "What\'s new" promo carousel on the Société account page',
  }, { tier: audienceLabel ?? tierName })

  const showViewAll = cards.length > VIEW_ALL_MIN_CARDS

  return (
    <Sheet background="light-background" padding="24 0" tabletPadding="48 0">
      <LayoutContainer variant="medium">
        <Group direction="vertical" gap={24}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
            <Heading variant="heading3">{sectionHeading}</Heading>
            {showViewAll && <TextButton kind="tertiary" size="medium" to={`/${LOYALTY_OFFERS_HUB_PAGE}`}>
              {intl.formatMessage({
                id: 'p0dE7G3AQEjKShJ_YDSr',
                defaultMessage: 'View all',
                description: 'Action button label to view all items in a carousel',
              })}
            </TextButton>}
          </Group>
          <CardCarousel pageSize="auto">
            {cards.map(card => {
              const isExternalCta = isExternalUrl(card.ctaUrl)
              return <Card
                key={`${card.imageId}-${card.ctaUrl}`}
                to={isExternalCta ? undefined : removeRegionFromPath(card.ctaUrl)}
                href={isExternalCta ? card.ctaUrl : undefined}
                target={card.openInNewTab ? '_blank' : undefined}
                rel={card.openInNewTab ? 'noopener noreferrer' : undefined}
              >
                <CardImage>
                  <Image image={{ id: card.imageId }} height={130} fit="center" {...card.imageOptions} />
                </CardImage>
                <CardBody>
                  <Group direction="vertical" gap={4}>
                    <Caption variant="small" format="uppercase" colour="neutral-two">{card.overline}</Caption>
                    <BodyText variant="large" weight="bold">{card.title}</BodyText>
                    <BodyText variant="small" colour="light-foreground-alt">{card.description}</BodyText>
                  </Group>
                  <CardCta>
                    <TextLink size="medium" weight="bold">
                      {card.ctaLabel}
                    </TextLink>
                  </CardCta>
                </CardBody>
              </Card>
            })}
          </CardCarousel>
        </Group>
      </LayoutContainer>
    </Sheet>
  )
}

export default LoyaltyWhatsNewCarousel
